import type { DeliveryEstimateDatesResponse, DeliveryEstimateResponse } from "@interfaces/heap-api";
import { MARKS, type Document, type TopLevelBlock } from "@contentful/rich-text-types";

export const ESTIMATED_DELIVERY_HEADING = "estimated-delivery";
// A/B selector identifier, separate from the CMS heading "estimated-delivery-vb".
export const ESTIMATED_DELIVERY_HEADING_VB = "edd-vb";

export const isEstimatedDeliveryHeading = (heading?: string): boolean =>
    heading?.trim().toLowerCase() === ESTIMATED_DELIVERY_HEADING;

export const isEstimatedDeliveryHeadingVB = (heading?: string): boolean =>
    heading?.trim().toLowerCase() === ESTIMATED_DELIVERY_HEADING_VB;

// If a sku is selected, use its own estimate. Otherwise, fall back to whichever sku is
// "earliest" (soonest delivery any size can offer, e.g. PDP before a size is picked) or
// "latest" (whichever sku gates the order, e.g. the bag/checkout), per `unselectedFallback`.
export const pickEstimatedDeliveryDate = (
    estimates: DeliveryEstimateResponse[] | undefined,
    selectedSku?: string | null,
    unselectedFallback: "earliest" | "latest" = "latest"
): { earliest: string; latest?: string } | null => {
    if (!estimates || estimates.length === 0) {
        return null;
    }

    if (selectedSku) {
        const match = estimates.find((estimate) => estimate.sku === selectedSku);
        return match?.estimate ? toRange(match.estimate) : null;
    }

    let candidateEstimate: DeliveryEstimateDatesResponse | undefined;

    for (const { estimate } of estimates) {
        const date = estimate?.deliveryDate;
        if (!date) continue;

        const isBetterCandidate =
            !candidateEstimate?.deliveryDate ||
            (unselectedFallback === "latest"
                ? date > candidateEstimate.deliveryDate
                : date < candidateEstimate.deliveryDate);

        if (isBetterCandidate) candidateEstimate = estimate;
    }

    return candidateEstimate ? toRange(candidateEstimate) : null;
};

const toRange = (
    estimate: DeliveryEstimateDatesResponse
): { earliest: string; latest?: string } | null => {
    if (!estimate.deliveryDate) {
        return null;
    }

    const earliest = estimate.earliestDeliveryDate ?? estimate.deliveryDate;
    const latest = estimate.latestDeliveryDate ?? estimate.deliveryDate;

    return earliest === latest ? { earliest } : { earliest, latest };
};

export const parseIsoDate = (value: string): Date | null => {
    const [year, month, day] = value.split("-").map(Number);
    const parsedDate = new Date(year, month - 1, day);

    const isValidDate =
        !isNaN(parsedDate.getTime()) &&
        parsedDate.getFullYear() === year &&
        parsedDate.getMonth() === month - 1 &&
        parsedDate.getDate() === day;

    return isValidDate ? parsedDate : null;
};

const formatDayMonth = (
    value: string,
    locale: string,
    options?: Intl.DateTimeFormatOptions
): string => {
    const parsed = parseIsoDate(value);
    if (!parsed) return value;

    return new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        ...options,
    }).format(parsed);
};

export const formatEstimatedDeliveryDate = (
    date: { earliest: string; latest?: string },
    locale: string,
    options?: Intl.DateTimeFormatOptions
): string => {
    const formatter = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" });

    if (!date.latest) {
        return formatDayMonth(date.earliest, locale, options);
    }

    const earliestParsed = parseIsoDate(date.earliest);
    const latestParsed = parseIsoDate(date.latest);

    if (!earliestParsed || !latestParsed) {
        // invalid date(s): preserve original values
        return `${date.earliest} - ${date.latest}`;
    }

    return formatter.formatRange(earliestParsed, latestParsed);
};

/**
 * Appends a bold date string to the end of the last block of a rich text document,
 */
export const appendEstimatedDeliveryDate = (document: Document, date: string): Document => {
    const content = [...document.content];
    const lastBlockIndex = content.length - 1;
    const lastBlock = content[lastBlockIndex] as TopLevelBlock;

    if (!lastBlock || !("content" in lastBlock)) {
        return document;
    }

    content[lastBlockIndex] = {
        ...lastBlock,
        content: [
            ...lastBlock.content,
            { nodeType: "text", value: ` ${date}`, marks: [{ type: MARKS.BOLD }], data: {} },
        ],
    } as TopLevelBlock;

    return { ...document, content };
};
